import { useMemo } from "react";
import moment, { Moment } from "moment-timezone";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  BookingWindowSettings,
  TenantConfigService,
} from "../../../../types/tenantConfig";
import {
  resolveBookingWindowSettings,
  useActiveProductCategory,
} from "../../../../util/categoryConfig";
import {
  pickedDayLabel,
  STATION_TIME_ZONE,
  stationDayLabel,
} from "../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";
import { spaceOf } from "../../../../util/useCases";

/**
 * The booking window a use case runs under — how far ahead a customer must
 * book, how far out they may look, and (read by the backend, not here) how long
 * a unit rests after a booking ends.
 *
 * ── THIS EXPLAINS A RULE; IT DOES NOT OWN ONE (ADR-0020) ──────────────────
 *
 * The dates this hook greys out are the dates the app API refuses. The
 * authoritative definition lives behind the API, in
 * `backend/src/core/lib/bookingWindow.ts`; this copy exists so a customer
 * learns the rule from a disabled date rather than from a 400 after filling in
 * a form. If the two disagree, this one is wrong.
 *
 * ── ONE RULE, FOUR DEPLOYABLES (ADR-0014) ─────────────────────────────────
 *
 * None of the four can import another, so they name each other and each is
 * pinned by its own tests:
 *
 *   - `new-backend/admin/src/lib/bookingWindow.ts` — canonical; write-side
 *     validation and the stored shape.
 *   - `backend/src/core/lib/bookingWindow.ts` — enforcement + availability.
 *   - `frontend/admin-ui/src/pages/services/bookingWindowRules.ts` — the
 *     editor's bounds and unit conversions.
 *   - THIS FILE — the reader. Pinned by `useBookingWindow.test.ts`.
 *
 * Change a default and change all four in the same PR — otherwise this app
 * offers dates its own API refuses.
 *
 * ── Why the defaults live here at all ─────────────────────────────────────
 *
 * The public config endpoint passes the STORED block through rather than
 * resolving it, because this app needs the default table regardless: LEGACY
 * tenants (kolula, neuland, demo, …) are pinned builds that never call that
 * endpoint and read `src/tenants/<tenant>/config.ts` instead. Resolving
 * server-side as well would add a copy of the same numbers for no reader that
 * lacks them.
 */

/** Minutes in a day. */
export const MINUTES_PER_DAY = 1440;

/**
 * Use cases with a whole-day booking window (the description's
 * `space.windowInDays`, which replaced `FULL_DAY_USE_CASES`). The lead buffer
 * is applied at DAY granularity for these — `now + lead`, floored to the start
 * of that day — which is exactly what the old hardcoded
 * `moment().add(5, "days").startOf("day")` produced on the two room screens.
 */
export const isFullDayUseCase = (useCase: string): boolean =>
  spaceOf(useCase)?.windowInDays === true;

/**
 * The DEFAULT zone the two boundaries are evaluated in.
 *
 * Never browser-local, so this app and the API that enforces the rule floor
 * the same day. Since #3219 the boundaries are evaluated in the zone of the
 * location being booked — `useBookingWindow` reads it through
 * `useSelectedLocationTimeZone`, the API through the location's `time_zone`
 * — and this is the zone of a location that has none.
 */
export const BOOKING_WINDOW_ZONE = STATION_TIME_ZONE;

export interface ResolvedBookingWindow {
  /** Minimum notice before a booking may start, in minutes. */
  leadMinutes: number;
  /** Latest bookable date, in whole months ahead (end of that month). */
  horizonMonths: number;
  /** How long a unit rests after a booking ends, in minutes. Read by the
   *  availability endpoint; this app never applies it itself. */
  followUpMinutes: number;
  /** Shortest stay a customer may book, in nights. Apartments only; 1 for
   *  every other use case. Mirror of `hasMinStay` in the canonical copy. */
  minNights: number;
  /** Earliest date/instant a customer may book — the lead buffer, applied. */
  earliest: Moment;
  /** Latest date a customer may book — the horizon, applied. */
  latest: Moment;
}

/**
 * Defaults per use case — a mirror of the canonical table in
 * `new-backend/admin/src/lib/bookingWindow.ts` (and the enforcing copy in
 * `backend/src/core/lib/bookingWindow.ts`). They must agree: the API refuses
 * what lies inside the lead time, so a longer lead here greys out dates the
 * API would accept, and a shorter one offers dates it refuses.
 *
 * `apartment` and `venue` carry two days' lead and an eighteen-month horizon;
 * `apartment` also rests one day after a booking (PO, 2026-09-23 — the first
 * deploy carried five days, which this copy kept until 2026-10-13).
 *
 * Everything else gets no lead and a one-month horizon, which is what the
 * station screens request (`add(1, "months").endOf("month")`).
 *
 * The follow-up buffer of a full-day use case is the description's
 * `space.followUpDefaultDays` (0 for venue, 1 for apartment) — it replaced
 * the separate apartment block.
 */
const FULL_DAY_DEFAULT = { leadMinutes: 2 * MINUTES_PER_DAY, horizonMonths: 18, followUpMinutes: 0, minNights: 1 };
const SUB_DAY_DEFAULT = { leadMinutes: 0, horizonMonths: 1, followUpMinutes: 0, minNights: 1 };

/** A minimum stay applies — the description's `space.minStay` (was `MIN_STAY_USE_CASES`). */
const hasMinStay = (useCase: string): boolean => spaceOf(useCase)?.minStay === true;

export const defaultBookingWindow = (
  useCase: string
): Omit<ResolvedBookingWindow, "earliest" | "latest"> =>
{
  const space = spaceOf(useCase);
  if (!space?.windowInDays) return { ...SUB_DAY_DEFAULT };
  return { ...FULL_DAY_DEFAULT, followUpMinutes: space.followUpDefaultDays * MINUTES_PER_DAY };
};

/** A horizon of zero would read as "unlimited" and mean "the rest of this
 *  month", so one month is the floor. There is no unlimited. */
export const MIN_HORIZON_MONTHS = 1;

/**
 * Resolve the stored settings against the defaults, and compute both ends of
 * the window.
 *
 * Pure, and exported separately from the hook so it can be exercised without a
 * store — and so the `now` it reads can be pinned by a test. Falls back FIELD
 * BY FIELD: a corrupt `horizonMonths` must not take the lead buffer with it.
 *
 * Both ends are computed in `now`'s zone — the location's (#3219): the day a
 * full-day lead floors to and the month the horizon ends in are the
 * location's.
 */
export const resolveBookingWindow = (
  stored: BookingWindowSettings | undefined,
  useCase: string,
  now: Moment = moment().tz(BOOKING_WINDOW_ZONE)
): ResolvedBookingWindow => {
  const fallback = defaultBookingWindow(useCase);
  const usable = (value: unknown, min: number): number | null =>
    typeof value === "number" && Number.isInteger(value) && value >= min ? value : null;

  const leadMinutes = usable(stored?.leadMinutes, 0) ?? fallback.leadMinutes;
  const horizonMonths =
    usable(stored?.horizonMonths, MIN_HORIZON_MONTHS) ?? fallback.horizonMonths;
  const followUpMinutes = usable(stored?.followUpMinutes, 0) ?? fallback.followUpMinutes;
  const minNights = hasMinStay(useCase)
    ? usable(stored?.minNights, 1) ?? fallback.minNights
    : fallback.minNights;

  const leadPoint = now.clone().add(leadMinutes, "minutes");

  return {
    leadMinutes,
    horizonMonths,
    followUpMinutes,
    minNights,
    // A full-day use case books a DATE, so the floor is the start of the day
    // the lead buffer lands in. A sub-day use case books a MOMENT, so it is
    // that moment. Both mirror `earliestBookableStart` on the API side.
    earliest: isFullDayUseCase(useCase) ? leadPoint.startOf("day") : leadPoint,
    // `endOf('month')`, not a day count — that is what all five client copies
    // computed, and expressing it in days would move the horizon for every
    // tenant on deploy day.
    latest: now.clone().add(horizonMonths, "months").endOf("month"),
  };
};

/**
 * Whether a picked calendar day lies outside the window — before the station
 * day `earliest` falls on, or after the one `latest` falls on.
 *
 * Compares day LABELS: the day the customer clicked (`pickedDayLabel`, read in
 * the phone's zone, where the picker built it) against the station's day of
 * each boundary. The screens compared the picked PHONE midnight with the
 * Berlin instants instead, so a phone east of Berlin greyed out the earliest
 * bookable day (Tokyo's 22.10. midnight is the Berlin 21.10., before the
 * 22.10. floor) and a phone west of it greyed out the last one (New York's
 * 30.04. midnight is 06:00 Berlin, after the 30.04. 00:00 cap).
 */
export const isOutsideBookableDays = (
  pickedDay: Moment,
  earliest?: Moment | null,
  latest?: Moment | null
): boolean => {
  const day = pickedDayLabel(pickedDay);
  return (
    (!!earliest && day < boundDayLabel(earliest)) ||
    (!!latest && day > boundDayLabel(latest))
  );
};

/**
 * A boundary's station day, read in the zone the boundary carries — the
 * location's, as `useBookingWindow` builds it (#3219) — so no screen threads
 * the zone through to its date picker. A bound without a zone is read in
 * `BOOKING_WINDOW_ZONE`, as every bound was before.
 */
const boundDayLabel = (bound: Moment): string =>
  stationDayLabel(bound, bound.tz() ?? BOOKING_WINDOW_ZONE);

/**
 * This tenant's booking window for a use case.
 *
 * Memoised on the stored entry so the two ends are stable across renders — a
 * fresh `moment()` on every render would make `earliest`/`latest` new objects
 * each time and retrigger any effect that depends on them.
 *
 * Evaluated in the zone of the location being booked (#3219): `timeZone` when
 * the screen holds its own location (the room week), otherwise the booking
 * flow's (`useSelectedLocationTimeZone`). The returned `earliest`/`latest`
 * carry that zone.
 */
export const useBookingWindow = (
  useCase: TenantConfigService | string,
  timeZone?: string
): ResolvedBookingWindow => {
  // ADR-0026 §B: category first, use case as the fallback. Resolved inside the
  // hook so every screen that already asks for a booking window gets the
  // category's without being edited.
  const categoryKey = useActiveProductCategory();
  const stored = useSelector((state: RootState) =>
    resolveBookingWindowSettings(state.tenantConfig.config, useCase, categoryKey)
  );

  const selectedZone = useSelectedLocationTimeZone();
  const zone = timeZone ?? selectedZone;

  return useMemo(
    () => resolveBookingWindow(stored, useCase, moment().tz(zone)),
    [stored, useCase, zone]
  );
};

export default useBookingWindow;
